/**
 * After the checkout (`/booked/:bookingId`, web-sign-in-checkout D7): the
 * booked page for a booking that goes ahead, the request-sent page for a
 * request the studio has not approved yet (and after it is withdrawn here),
 * and Pay now for an approved request still to be paid; any other booking
 * opens on the client's booking page. Signed out, the sign-in step;
 * a booking that is not this client's shows the studio's not-found page.
 */
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { useWebSession } from '../api/session';
import { useSiteStudio } from '../api/site';
import { useWebBooking } from '../hooks/useWebBooking';
import { dispatchPageEvent } from '../kit/pageEvents';
import { MoveTo, useSiteBack } from '../pageMover';
import { PhoneBooked, PhoneRequestSent } from '../phone/PhoneBooked';
import { PhoneSignIn } from '../phone/PhoneCheckout';
import { Booked } from '../pieces/booked';
import { RequestSent } from '../pieces/request-sent';
import { SignInStep } from '../pieces/sign-in-step';
import booked from '../pieces/booked/Booked.module.css';
import shared from '../styles/shared.module.css';
import { useAppScreens } from '../ui/useIsPhone';
import { usePageTitle } from '../ui/usePageTitle';
import { NotFoundPage } from './NotFoundPage';

export function BookedPage() {
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const session = useWebSession();
  const { detail } = useWebBooking(bookingId);
  const sawRequest = useRef(false);
  const phone = useAppScreens();
  const studio = useSiteStudio().data;
  const onBack = useSiteBack();
  usePageTitle('Your booking');
  // web-studio-sites D4: a custom page hears a booking that went through, once per booking.
  const confirmed = detail.data?.status === 'CONFIRMED' || detail.data?.status === 'COMPLETED';
  useEffect(() => {
    if (confirmed) dispatchPageEvent('telify:booked', { bookingId });
  }, [confirmed, bookingId]);

  if (session.status === 'signedOut') {
    // On a phone, the app's frame before the booking can be read (row K11, open item 4): the studio's About photo, "Your booking", no name.
    if (phone) {
      return (
        <PhoneSignIn
          hero={{ imageUrl: studio?.aboutPhotoUrl ?? null, focalPoint: { x: null, y: null }, name: '', providerName: '', providerPhoto: null, whenLabel: null }}
          onBack={onBack}
        />
      );
    }
    return (
      <div className={`${shared.wrap} ${booked.signInWrap}`}>
        <SignInStep />
      </div>
    );
  }
  if (detail.isError) return <NotFoundPage />;
  const status = detail.data?.status;
  if (!status) return null;
  if (status === 'PENDING_APPROVAL') sawRequest.current = true;
  if (status === 'PENDING_APPROVAL' || (status === 'CANCELLED' && sawRequest.current)) return phone ? <PhoneRequestSent /> : <RequestSent />;
  const id = encodeURIComponent(bookingId);
  // An approved request that still owes its payment is paid on Pay now.
  if (status === 'PENDING_PAYMENT' && detail.data?.payment?.amount) return <MoveTo to={`/account/bookings/${id}/pay`} />;
  // Below 768px, the app's end screen (design D12).
  if (status === 'CONFIRMED' || status === 'COMPLETED') return phone ? <PhoneBooked /> : <Booked />;
  // Anything else (cancelled before this page opened, a no-show, a waitlist place) lives on the client's booking page.
  return <MoveTo to={`/account/bookings/${id}`} />;
}
